export const TRAINING_ASSIGNMENT_LIMITS = {
  maxTitleLength: 200,
  maxTargets: 50,
  maxIdempotencyKeyLength: 100,
  maxInviteSubjectLength: 200,
  maxInviteMessageLength: 2000,
  maxCcEmails: 10,
} as const;

/** What the author chose for the invitation email, on top of the standard template. */
export interface TrainingInvitationSettings {
  /** `null` means the standard subject. */
  inviteSubject: string | null;
  /** Plain text, shown escaped above the Start button. `null` means none. */
  inviteMessage: string | null;
  /** Copy each person's manager, read from user-service when the message is queued. */
  ccManagers: boolean;
  /** Always copied; never the person the message is addressed to. */
  ccEmails: string[];
  /** Copy the manager on overdue reminders only, when `ccManagers` is off. */
  ccManagersOnOverdue: boolean;
}

export const ASSIGNMENT_STATUSES = { open: 'open', closed: 'closed' } as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[keyof typeof ASSIGNMENT_STATUSES];

/** What the author picked: one person, a training group, or a department. */
export const ASSIGNMENT_TARGET_KINDS = {
  user: 'user',
  trainingGroup: 'training_group',
  department: 'department',
} as const;
export type AssignmentTargetKind =
  (typeof ASSIGNMENT_TARGET_KINDS)[keyof typeof ASSIGNMENT_TARGET_KINDS];

/** How a person came to be assigned. `direct` is a person picked by name. */
export const ASSIGNEE_SOURCES = {
  direct: 'direct',
  trainingGroup: 'training_group',
  department: 'department',
} as const;
export type AssigneeSource = (typeof ASSIGNEE_SOURCES)[keyof typeof ASSIGNEE_SOURCES];

/** Written by the service, never derived. `cancelled` is a removal or a closed assignment. */
export const ASSIGNEE_STATES = {
  assigned: 'assigned',
  inProgress: 'in_progress',
  submitted: 'submitted',
  passed: 'passed',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;
export type AssigneeState = (typeof ASSIGNEE_STATES)[keyof typeof ASSIGNEE_STATES];

/** A person who still has something to do, and so may be reminded. */
export const OPEN_ASSIGNEE_STATES: readonly AssigneeState[] = [
  ASSIGNEE_STATES.assigned,
  ASSIGNEE_STATES.inProgress,
];

export type AssigneeStateCounts = Record<AssigneeState, number>;

export interface AssignmentTargetInput {
  kind: AssignmentTargetKind;
  targetId: string;
}

export interface AssignmentTarget {
  id: string;
  kind: AssignmentTargetKind;
  targetId: string;
  /** A snapshot taken at creation, so a rename never rewrites what was assigned. */
  targetName: string;
}

export interface TrainingAssignmentSummary {
  id: string;
  title: string;
  questionnaireId: string;
  questionnaireCode: string;
  questionnaireTitle: string;
  versionNumber: number;
  dueAt: string;
  status: AssignmentStatus;
  createdById: string;
  createdAt: string;
  total: number;
  counts: AssigneeStateCounts;
}

export interface TrainingAssignee {
  id: string;
  userId: string;
  /** A snapshot taken when the person was assigned. */
  fullName: string;
  source: AssigneeSource;
  sourceTargetId: string;
  state: AssigneeState;
  inviteCount: number;
  invitedAt: string | null;
  lastReminderAt: string | null;
  completedAt: string | null;
}

export interface TrainingAssignmentDetail
  extends TrainingAssignmentSummary,
    TrainingInvitationSettings {
  /** Read live; `null` when user-service could not say. */
  createdByName: string | null;
  targets: AssignmentTarget[];
  assignees: TrainingAssignee[];
}

export interface ListTrainingAssignmentsQuery {
  status?: AssignmentStatus;
  search?: string;
}

export interface CreateTrainingAssignmentInput extends Partial<TrainingInvitationSettings> {
  questionnaireId: string;
  title: string;
  dueAt: string;
  targets: AssignmentTargetInput[];
  /** A repeat of the same key returns the first assignment and queues no second invitation. */
  idempotencyKey: string;
}

export interface CreateTrainingAssignmentPayload {
  input: CreateTrainingAssignmentInput;
  actorId: string;
}

export interface PreviewTrainingAssignmentInput {
  targets: AssignmentTargetInput[];
}

/** Who a set of targets reaches today, with a person reached twice counted once. */
export interface TrainingAssignmentPreview {
  total: number;
  bySource: Record<AssigneeSource, number>;
}

/** Only the fields present change; an explicit `null` subject or message clears it. */
export type UpdateTrainingInvitationInput = Partial<TrainingInvitationSettings>;

export interface UpdateTrainingInvitationPayload {
  id: string;
  input: UpdateTrainingInvitationInput;
}

export interface PreviewTrainingInvitationInput {
  /** Names the questionnaire in the sample; a placeholder title is used when absent. */
  questionnaireId?: string;
  /** An ISO date; a placeholder date is used when absent. */
  dueAt?: string;
  inviteSubject?: string | null;
  inviteMessage?: string | null;
}

export interface PreviewTrainingInvitationPayload {
  input: PreviewTrainingInvitationInput;
  actorId: string;
}

/** The invitation exactly as it would be sent to a sample person. */
export interface TrainingInvitationPreview {
  subject: string;
  /** The subject when the author has chosen none. */
  standardSubject: string;
  html: string;
  text: string;
}

export interface TrainingAssignmentIdPayload {
  id: string;
}

export interface RemoveTrainingAssigneePayload extends TrainingAssignmentIdPayload {
  assigneeId: string;
}

export interface AddTrainingAssigneesInput {
  targets: AssignmentTargetInput[];
}

export interface AddTrainingAssigneesPayload extends TrainingAssignmentIdPayload {
  input: AddTrainingAssigneesInput;
}

export interface AddTrainingAssigneesResult {
  assignment: TrainingAssignmentDetail;
  /** People newly assigned, or assigned again after a removal. Each was invited. */
  added: number;
  /** People the targets reached who already hold the assignment. */
  alreadyAssigned: number;
}

export interface RemindTrainingAssigneesResult {
  assignment: TrainingAssignmentDetail;
  /** How many reminders were queued. */
  reminded: number;
  /** Open people skipped because they were reminded within the last day. */
  skippedRecent: number;
}

export const TRAINING_ASSIGNMENT_ERROR_CODES = {
  /** 422: the targets reach nobody active. */
  noPeople: 'TRAINING_ASSIGNMENT_NO_PEOPLE',
  /** 409: the questionnaire has no published version to assign. */
  versionNotPublished: 'TRAINING_ASSIGNMENT_VERSION_NOT_PUBLISHED',
  /** 409: the assignment is closed. */
  closed: 'TRAINING_ASSIGNMENT_CLOSED',
  /** 422: a person picked by name does not exist or is not active. */
  personNotEligible: 'TRAINING_ASSIGNMENT_PERSON_NOT_ELIGIBLE',
  /** 409: the person has already submitted, so there is nothing left to remove them from. */
  assigneeNotOpen: 'TRAINING_ASSIGNEE_NOT_OPEN',
  /** 422: the due date is not in the future. */
  dueInPast: 'TRAINING_ASSIGNMENT_DUE_IN_PAST',
  /** 422: the subject or message is too long, or a copied address is not an address or is over the limit. */
  invalidInvitation: 'TRAINING_ASSIGNMENT_INVALID_INVITATION',
} as const;
